import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModalShell } from './menu';
import { DeviceDiagnostics } from './DeviceDiagnostics';
import { DeviceIdleCard } from './DeviceIdleCard';
import { DevicePowerCard } from './DevicePowerCard';
import { ContentSkeleton } from './skeleton/ContentSkeleton';
import { useCurrentOrgDeleted } from '../nav/useCurrentOrgDeleted';
import { DeviceEditForm, DeviceInfoCard, DeviceOperationsCard, OrgDeletedNotice, showsPowerCard, useDeviceRecord } from '../pages/DeviceDetail';

// =============================================================================
// DeviceDetailModal — 구성 관리 > 그룹·디바이스 목록에서 기기 상세를 화면 이동 없이 모달로 (2026-09-14, 대교).
//   view: 디바이스 정보 카드(위) → 기기 진단(아래, 접속·장애복구 이력은 접힘)
//   edit: 수정 폼. 페어링 직후(isNew)엔 '디바이스 추가'로 열려 이름·해상도를 받는다.
// 캘리브레이션은 전용 페이지(/devices/:id/calibration)라 모달을 닫고 이동한다.
// =============================================================================

export function DeviceDetailModal({
  deviceId,
  initialMode = 'view',
  isNew = false,
  groups,
  groupId = null,
  canEdit = true,
  onAssignGroup,
  onClose,
  onChanged,
}: {
  deviceId: string;
  initialMode?: 'view' | 'edit';
  /** 페어링 직후 — 폼 제목이 '디바이스 추가', 취소하면 모달을 닫는다. */
  isNew?: boolean;
  /** 실제 그룹 배정(device_groups) — 상세의 '그룹'과 수정 폼의 그룹 셀렉트에 쓴다. 옛 자유입력 groupLabel은 쓰지 않는다. */
  groups?: Array<{ id: string; name: string }>;
  groupId?: string | null;
  /** 수정·삭제·명령 버튼 노출 여부(기기 관리 권한). */
  canEdit?: boolean;
  onAssignGroup?: (groupId: string | null) => Promise<boolean>;
  onClose: () => void;
  /** 저장·삭제·갱신 뒤 목록을 새로 읽게 한다. */
  onChanged: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const record = useDeviceRecord(deviceId);
  const orgDeleted = useCurrentOrgDeleted();
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
  const [fresh, setFresh] = useState(isNew);
  const title = mode === 'edit' ? (fresh ? '디바이스 추가' : '디바이스 수정') : '디바이스 상세';

  // 추가·수정은 단일 입력열에 맞춘 md(560), 진단이 있는 상세는 lg(920).
  // 예전엔 panelClassName으로 max-h-[90vh]와 패널 자체 스크롤을 겹쳐 줘서, 1280×800에서
  // 수정 폼 아래쪽('캘리브레이션' 카드 등)이 패널 밖으로 잘렸다.
  return (
    <ModalShell title={title} onClose={onClose} size={mode === 'edit' ? 'md' : 'lg'}>
      {mode === 'view' ? <>
        {orgDeleted && <OrgDeletedNotice />}
        {!record.device && !record.err
          ? <ContentSkeleton variant="cards" label="디바이스 정보를 불러오는 중…" />
          : <DeviceInfoCard device={record.device} timelines={record.timelines} err={record.err} readOnly={orgDeleted || !canEdit}
            groupName={groups?.find((group) => group.id === groupId)?.name ?? null}
            onEdit={() => setMode('edit')} onDeleted={() => { onChanged(); onClose(); }} />}
        {record.device && !record.err && <DeviceOperationsCard className="shrink-0" device={record.device}
          readOnly={orgDeleted || !canEdit} />}
        {/* 모달 본문이 세로 플렉스라 shrink-0 이 없으면 카드가 눌려 사라진다. */}
        {record.device && !record.err && <DeviceIdleCard key={record.device.id} className="shrink-0" device={record.device}
          readOnly={orgDeleted || !canEdit} onChanged={() => { record.reload(); onChanged(); }} />}
        {record.device && !record.err && showsPowerCard(record.device) && <DevicePowerCard className="shrink-0" device={record.device}
          readOnly={orgDeleted || !canEdit} onChanged={() => { record.reload(); onChanged(); }} />}
        {record.device && !record.err && <DeviceDiagnostics className="" deviceId={record.device.id} onScopeBlocked={record.hideBlocked} />}
      </> : record.device ? (
        <DeviceEditForm device={record.device} isNew={fresh}
          {...(groups ? { groups, groupId, onAssignGroup } : {})}
          {...(canEdit && !orgDeleted ? { onDeleted: () => { onChanged(); onClose(); } } : {})}
          onSaved={() => { setFresh(false); record.reload(); onChanged(); setMode('view'); }}
          onCancel={() => { if (fresh) onClose(); else setMode('view'); }}
          onCalibrate={() => { onClose(); navigate(`/devices/${deviceId}/calibration`); }} />
      ) : record.err
        ? <p role="alert" className="rounded border border-red/50 bg-red/15 px-3 py-2 text-[14px] text-red">{record.err}</p>
        : <ContentSkeleton variant="form" label="디바이스 정보를 불러오는 중…" />}
    </ModalShell>
  );
}
